/**
 * Palette-surface semantics: which colour the widget actually paints on, and
 * the light/dark mode that follows from it.
 *
 * LEAF MODULE: imports nothing from `src/`.
 */

import { getLuminance } from '@mui/material/styles';

/** Below this, a surface counts as dark. Matches MUI's own light/dark split. */
const DARK_LUMINANCE_THRESHOLD = 0.5;

/**
 * The surface the widget paints on, in preference order: the ThemeEditor
 * "Karten → Hintergrund" colour (`palette.background.card`) when set, then the
 * widget window (`paper`), then the page behind it.
 *
 * The last two links only ever matter for a raw customer palette — a theme that
 * has been through `createTheme` always carries `background.paper`.
 */
export function surfaceColor(palette) {
  const background = palette?.background;
  return (
    background?.card ||
    background?.paper ||
    background?.default ||
    background?.page
  );
}

/**
 * Card surface colour resolver for `sx`, e.g.
 * `sx={{ backgroundColor: cardBackground }}`.
 *
 * Cards only diverge from the widget window once a distinct card colour is
 * chosen, so every theme predating the card token renders byte-identically.
 */
export const cardBackground = (theme) => surfaceColor(theme.palette);

/**
 * The light/dark mode the widget should render with.
 *
 * THE RULE: the COLOURS decide. An authored `palette.mode` is never trusted on
 * its own, in either direction.
 *
 * Both disagreements are real:
 *  - today, the ThemeEditor has no `mode` in its schema, so a theme built
 *    entirely from dark colours arrives as MUI's default `'light'`. Every
 *    mode-keyed default then takes the light path — black-on-dark input
 *    outlines, white event chips, `darken()` where `lighten()` was meant.
 *  - once the editor can author light and dark palettes independently, a
 *    customer can just as easily put light colours under a dark mode. The same
 *    rule hardens against that without a second mechanism.
 *
 * Scope discipline: derive flags the customer never authored, never override
 * colours they did. `mode` is an internal rendering flag nobody picks in the
 * editor, so computing it is free. Their text and surface colours are choices,
 * and contrast between them is the ThemeEditor's job (it has contrast badges) —
 * silently "correcting" a chosen colour here would mean the widget ignoring the
 * theme it was given.
 *
 * Returns `'light'` for a missing or unparseable colour, which keeps today's
 * rendering: `getLuminance` throws on the `var(--…)` strings the CssVars path
 * produces, and a theme must not fail to build over a colour it could not read.
 */
export function effectiveMode(palette) {
  try {
    return getLuminance(surfaceColor(palette)) < DARK_LUMINANCE_THRESHOLD
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
}

/**
 * Stamp the effective mode onto a theme, immediately before `createTheme`.
 * Everything downstream — ours, MUI's component defaults, and third-party MUI
 * components such as `mui-tel-input` that we cannot restyle — then reads
 * `palette.mode` normally and is correct by construction.
 *
 * Themes carrying `colorSchemes` are returned UNTOUCHED. That is the future
 * dark-mode path: each scheme authors its own mode and the selection between
 * them happens upstream, so collapsing it to one derived mode would break
 * exactly the feature this leaves room for. The exclusion lives here, beside
 * the rule, so that no call site can forget it.
 *
 * ⚠ That `colorSchemes` branch is UNREACHABLE today and therefore untested:
 * DomHandler builds colorSchemes themes via `extendTheme` without calling this
 * helper, so nothing can currently distinguish the guard from its absence. It
 * is deliberate defence for whoever wires up real dark mode — do not remove it
 * as dead code. Every other behaviour here is covered end-to-end by the mode
 * stories in components/handlers/DomHandler.tokens.stories.jsx.
 */
export function withEffectiveMode(theme) {
  if (!theme?.palette || theme.colorSchemes) return theme;
  return {
    ...theme,
    palette: { ...theme.palette, mode: effectiveMode(theme.palette) },
  };
}
